import {
  ConfigurationGroupAddAction,
  ConfigurationGroupListAction,
  ConfigurationsAddAction,
  ConfigurationsListAction,
  ConfigurationsUpdateAction,
  KalturaConfigurationGroup,
  KalturaConfigurationGroupListResponse,
  KalturaConfigurations,
  KalturaConfigurationsFilter,
  KalturaConfigurationsListResponse,
  KalturaPlatform,
} from '@kaltura-ott/ott-client-types';
import { Observable } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { kalturaClient } from '../../kaltura-clent';

import { KuxError } from './../../errors';
import { DmsAppsVersions, MediaTypes, DmsInterface } from './dms-interfaces';

/**
 * Array of predefined app versions.
 * Each item contains app name, client version and platform name.
 * @param name
 */
export function getDmsAppVersions(name: string): DmsAppsVersions[] {
  return [
    {
      appname: `com.Kaltura.${name}.web`,
      clientversion: '1.0.1',
      platform: KalturaPlatform.other,
    },
    {
      appname: `com.Kaltura.${name}.androidTV`,
      clientversion: '1.0.1',
      platform: KalturaPlatform.stb,
    },
    {
      appname: `com.Kaltura.${name}.androidTV`,
      clientversion: '1.0.1',
      platform: KalturaPlatform.android,
    },
    {
      appname: `com.Kaltura.${name}.ctv`,
      clientversion: '1.0.1',
      platform: KalturaPlatform.ctv,
    },
    {
      appname: `com.Kaltura.${name}.appletv`,
      clientversion: '1.0.1',
      platform: KalturaPlatform.ios,
    },
    {
      appname: `com.Kaltura.${name}.mobile`,
      clientversion: '1.0.1',
      platform: KalturaPlatform.ios,
    },
    {
      appname: `com.Kaltura.${name}.mobile`,
      clientversion: '1.0.1',
      platform: KalturaPlatform.android,
    },
  ];
}

/**
 * Generates default dms configuration based on parameters
 *
 * @param assetStructs
 * @param apiUrl
 * @param partnerId
 * @param rootCategoryID
 */
export function getDmsContract(
  assetStructs: MediaTypes,
  apiUrl: string,
  partnerId: string,
  rootCategoryID: string,
): DmsInterface {
  return {
    FilesFormat: {
      VOD: ['WEB_Devices_Main_DASH', 'mpegdash'],
      LIVE: ['STB_Devices_Main_DASH', 'mpegdash'],
      TRAILER: ['Trailer', 'applehttp'],
    },
    Gateways: {
      RestGW: apiUrl,
    },
    MediaTypes: assetStructs,
    staticAssetsUrl: 'https://kux-static-files.s3-us-west-2.amazonaws.com/',
    isforceupdate: false,
    ForcedUpdateURL: [
      {
        iOS: 'https://itunes.apple.com/app/',
      },
      {
        Android: 'https://play.google.com/store/apps/details?id=',
      },
      {
        TV_LG: 'https://lg.store.com',
      },
      {
        TV_Samsung: 'https://samsung.store.com/',
      },
    ],
    upgrade_window_start_time: '12:00',
    upgrade_window_end_time: '15:00',
    error_screen_retries_before_reboot_button: '1',
    error_screen_number_of_saved_failed_requests: '2',
    slow_connection_sampling_period: 300,
    consecutive_request_failures_limit: 2,
    player_buffer_size: 30,
    bitrate_limitation_management: {
      bitrate_limit_set_by: 'user',
      local_vq_settings: {
        '4k': {
          top_range_bitrate: 10,
        },
        hd: {
          top_range_bitrate: 4,
        },
        sd: {
          top_range_bitrate: 1.5,
        },
      },
      user_allowed_to: false,
    },
    EPGUpdatesFreqHours: '5',
    GID: partnerId,
    RootCategoryID: rootCategoryID,
    EULA: 'https://www.microsoft.com/en-us/windowsvirtualdesktop/eula',
    apiVersion: '5.4.0.28195',
    sentry: {
      dsn: 'https://95fd64f1ca68465d82d3bf505a3fe51a@o478551.ingest.sentry.io/5546374',
    },
    PlayerSettings: {
      SCRIPT_URL:
        'https://cdnapisec.kaltura.com/p/2654801/sp/265480100/embedPlaykitJs/uiconf_id/47122453/partner_id/2654801/',
      SCRIPT_ID: 'player-script',
      uiconf_id: '47122453',
      Youbora: {
        AccountCode: 'cetindev',
      },
    },
    ProfileInfo: {
      isProfileSupported: true,
      AvatarsFolder: '~/',
      NumberOfAvatar: 5,
      DefaulAvatarName: 'av1',
      SupportKidsProfile: 'true',
      maxParentalAgeKidsGroups: '12',
    },
    GDPR: {
      cookies_policy_url: 'https://www.o2.cz/servis/podminky-a-pravidla/cookies-1-en',
    },
    Suggested_recalculation_period: 1,
    Suggested_calculation_by_last_x_items: 10,
    hide_x_watched_items: 50,
    personallist_enums: {
      saved_interest_items_list: '3',
      chosen_search_list: '4',
      personal_list: '5',
    },
    loading_screen: {
      loading_bg_color: '#242540',
    },
    wrong_pin_tries_limit: 3,
    wrong_pin_timeout: 120,
  };
}

/**
 * Adds Default configuration group
 */
export function addDefaultConfigurationGroup(groupId: string): Observable<KalturaConfigurationGroup> {
  const client = kalturaClient();

  return client.request(
    new ConfigurationGroupAddAction({
      configurationGroup: new KalturaConfigurationGroup({ name: 'KONE_' + groupId, isDefault: true }),
    }),
  );
}

/**
 * Retreives Default configuration group
 */
export function getDefaultConfigurationGroup(): Observable<KalturaConfigurationGroup | undefined> {
  const client = kalturaClient();

  return client.request(new ConfigurationGroupListAction()).pipe(
    map((response: KalturaConfigurationGroupListResponse) => {
      return response.objects.find((cg) => cg.isDefault);
    }),
  );
}

/**
 * Retreives default configuration group
 * Retreives array of existing configurations by default configuration group id
 */
export function getDmsConfigurationsList(): Observable<KalturaConfigurations[]> {
  const client = kalturaClient();

  return getDefaultConfigurationGroup().pipe(
    mergeMap((defaultGroup) => {
      if (defaultGroup) {
        const action = new ConfigurationsListAction({
          filter: new KalturaConfigurationsFilter({
            configurationGroupIdEqual: defaultGroup.id,
          }),
        });

        return client.request(action).pipe(
          map((result: KalturaConfigurationsListResponse) => {
            if (result?.objects) {
              return result.objects;
            }
            throw new KuxError('DMS loading failed.');
          }),
          catchError((error) => {
            throw new KuxError('DMS loading failed.', error?.code);
          }),
        );
      } else {
        return [];
      }
    }),
  );
}

/**
 * Defines configurations for configuration group id
 *
 * @param groupId
 * @param version
 * @param assetStructs
 * @param apiUrl
 * @param partnerId
 * @param rootCategoryID
 */
function defineDmsConfigurationRequest(
  groupId: string,
  version: DmsAppsVersions,
  assetStructs: MediaTypes,
  apiUrl: string,
  partnerId: string,
  rootCategoryID: string,
): Observable<KalturaConfigurations> {
  const content = getDmsContract(assetStructs, apiUrl, partnerId, rootCategoryID);

  const action = new ConfigurationsAddAction({
    configurations: new KalturaConfigurations({
      configurationGroupId: groupId,
      appName: version.appname,
      clientVersion: version.clientversion,
      platform: version.platform,
      content: JSON.stringify(content),
    }),
  });
  const client = kalturaClient();
  return client.request(action).pipe(
    map((result) => {
      return result;
    }),
    catchError((error) => {
      throw new KuxError('DMS adding failed.', error?.code);
    }),
  );
}

/**
 * Defines configuration based on dms contract
 *
 * Retreives default configuration group
 * If default group exists, defines configurations for default configuration group id
 * If not, creates default configuration group and defines configurations for the newly created group
 *
 * @param version
 * @param assetStructs
 * @param apiUrl
 * @param partnerId
 * @param rootCategoryID
 */
export function defineDmsConfiguration(
  version: DmsAppsVersions,
  assetStructs: MediaTypes,
  apiUrl: string,
  partnerId: string,
  rootCategoryID: string,
): Observable<KalturaConfigurations> {
  return getDefaultConfigurationGroup().pipe(
    mergeMap((defaultGroup) => {
      if (defaultGroup) {
        return defineDmsConfigurationRequest(defaultGroup.id, version, assetStructs, apiUrl, partnerId, rootCategoryID);
      } else {
        return addDefaultConfigurationGroup(partnerId).pipe(
          mergeMap((group) => {
            return defineDmsConfigurationRequest(group.id, version, assetStructs, apiUrl, partnerId, rootCategoryID);
          }),
        );
      }
    }),
  );
}

/**
 * Retreives default configuration group
 * If default configuration group exists, updates given configuration by id
 *
 * @param id Id of configuration to update
 * @param version Contains appName, clientVersion and platform
 * @param content json content of conifiguration
 */
export function updateDmsConfiguration(
  id: string,
  version: DmsAppsVersions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any,
): Observable<KalturaConfigurations> {
  return getDefaultConfigurationGroup().pipe(
    mergeMap((defaultGroup) => {
      if (defaultGroup) {
        const action = new ConfigurationsUpdateAction({
          id,
          configurations: new KalturaConfigurations({
            configurationGroupId: defaultGroup.id,
            appName: version.appname,
            clientVersion: version.clientversion,
            platform: version.platform,
            content: JSON.stringify(content),
          }),
        });
        const client = kalturaClient();
        return client.request(action).pipe(
          map((result) => {
            return result;
          }),
          catchError((error) => {
            throw new KuxError('DMS adding failed.', error?.code);
          }),
        );
      } else {
        throw new KuxError('DMS adding failed.');
      }
    }),
  );
}

/**
 * Retreives default configuration group
 * If default configuration group exists, add configuration
 *
 * @param version Contains appName, clientVersion and platform
 * @param content json content of conifiguration
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function addDmsConfiguration(version: DmsAppsVersions, content: any): Observable<KalturaConfigurations> {
  return getDefaultConfigurationGroup().pipe(
    mergeMap((defaultGroup) => {
      if (defaultGroup) {
        const action = new ConfigurationsAddAction({
          configurations: new KalturaConfigurations({
            configurationGroupId: defaultGroup.id,
            appName: version.appname,
            clientVersion: version.clientversion,
            platform: version.platform,
            content: JSON.stringify(content),
          }),
        });
        const client = kalturaClient();
        return client.request(action).pipe(
          map((result) => {
            return result;
          }),
          catchError((error) => {
            throw new KuxError('DMS adding failed.', error?.code);
          }),
        );
      } else {
        throw new KuxError('DMS adding failed.');
      }
    }),
  );
}
